/**
 * Knowledge base (QM-D07-001 / QM-D07-003, Pending Build item 15) via /v1/knowledge/*: the tenant's sources with
 * status and scanned-file (OCR) state, plus websites: add a site, watch crawl progress and per-page status, read
 * again, stop, change the schedule, remove. Websites and OCR retry show only when the flags knowledge.crawler /
 * knowledge.ocr are on for the tenant (GET /v1/flags/me); the server enforces the flags and roles either way.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Badge, Button, DataTable, EmptyState, ErrorState, Field, Heading, Loading, Panel, Select, Text, TextInput } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff, BffError, bffJson } from '../../../lib/bff';
import { useFlags } from '../../../lib/flags';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';

type Tone = 'neutral' | 'accent' | 'warning' | 'error' | 'success';
interface Ocr { status: string; reason: string | null; pages: number; engine: string | null }
export interface Source { id: string; type: string; title: string; url?: string | null; status: string; statusReason: string | null; chunkCount: number; lastIngestedAt: string | null; ocr?: Ocr | null }
export interface Site {
  id: string; sourceId: string; rootUrl: string; status: string; statusReason: string | null; maxPages: number; recrawlDays: number | null;
  pages: { found: number; indexed: number; unchanged: number; skipped: number; failed: number }; nextCrawlAt: string | null;
}
interface SitePage { id: string; url: string; status: string; reason: string | null; httpStatus: number | null }

export const RECRAWL_OPTIONS = [7, 14, 30, 90] as const;
const REMOVERS = ['customer_owner', 'customer_admin'];

export const siteTone = (s: string): Tone => (s === 'ready' ? 'success' : s === 'failed' ? 'error' : s === 'cancelled' ? 'warning' : s === 'crawling' || s === 'queued' ? 'accent' : 'neutral');
export const isBusy = (sites: Site[], sources: Source[]) =>
  sites.some((s) => s.status === 'crawling' || s.status === 'queued') || sources.some((s) => s.ocr?.status === 'queued' || s.ocr?.status === 'running');
export const pagesDone = (s: Site) => s.pages.indexed + s.pages.unchanged + s.pages.skipped + s.pages.failed;

/** Translation key (and params) for a page note: http_<code> reasons carry the code, unknown reasons fall back. */
export function pageReasonKey(reason: string | null, known: (k: string) => boolean): { key: string; params?: Record<string, string> } | null {
  if (!reason) return null;
  if (reason.startsWith('same_as:')) return null;
  const http = /^http_(\d{3})$/.exec(reason);
  if (http) return { key: 'knowledge.pages.reason.http', params: { code: http[1]! } };
  const key = `knowledge.pages.reason.${reason}`;
  return known(key) ? { key } : { key: 'knowledge.pages.reason.other' };
}

function useKnowledgeFlags() {
  const flag = useFlags();
  return { crawler: flag('knowledge.crawler') === 'on', ocr: flag('knowledge.ocr') === 'on' };
}

export function KnowledgePage() {
  const { t, me } = useApp();
  const flags = useKnowledgeFlags();
  const sources = useResource<Source[]>('/v1/knowledge/sources');
  const sites = useResource<Site[]>(flags.crawler ? '/v1/knowledge/sites' : null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const canRemove = (me?.roles ?? []).some((r) => REMOVERS.includes(r));
  const known = (k: string) => t(k) !== k;

  const siteList = sites.status === 'ready' ? sites.data : [];
  const sourceList = sources.status === 'ready' ? sources.data : [];
  const polling = isBusy(siteList, sourceList);
  const { reload: reloadSources } = sources;
  const { reload: reloadSites } = sites;
  useEffect(() => {
    if (!polling) return;
    const h = setInterval(() => { reloadSources(); reloadSites(); }, 5000);
    return () => clearInterval(h);
  }, [polling, reloadSources, reloadSites]);

  const errorText = (e: unknown) => {
    const d = e instanceof BffError ? (e.envelope?.error.details as { messageKey?: string } | undefined) : undefined;
    return d?.messageKey && known(d.messageKey) ? t(d.messageKey) : t('knowledge.sites.error.generic');
  };
  const run = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMessage(null);
    try { await fn(); setMessage(t(ok)); reloadSources(); reloadSites(); } catch (e) { setMessage(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <div className="qm-page__head">
        <div>
          <Heading level={1} id="page-title">{t('knowledge.page.title')}</Heading>
          <Text tone="muted">{t('knowledge.page.intro')}</Text>
        </div>
      </div>
      <p role="status" aria-live="polite" data-testid="knowledge-message">{message ?? ''}</p>
      {flags.crawler ? <SitesPanel sites={sites} busy={busy} run={run} canRemove={canRemove} /> : null}
      <SourcesPanel sources={sources} busy={busy} run={run} canRemove={canRemove} ocr={flags.ocr} />
    </section>
  );
}

type Run = (fn: () => Promise<unknown>, ok: string) => Promise<void>;

function SitesPanel({ sites, busy, run, canRemove }: { sites: ReturnType<typeof useResource<Site[]>>; busy: boolean; run: Run; canRemove: boolean }) {
  const { t } = useApp();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [schedule, setSchedule] = useState('');
  const add = (e: FormEvent) => {
    e.preventDefault();
    if (!url.trim()) return;
    const body = { url: url.trim(), ...(name.trim() ? { title: name.trim() } : {}), recrawlDays: schedule ? Number(schedule) : null };
    void run(() => bffJson('/v1/knowledge/sites', 'POST', body), 'knowledge.sites.added').then(() => { setUrl(''); setName(''); });
  };
  return (
    <Panel aria-labelledby="sites-title">
      <Heading level={2} id="sites-title">{t('knowledge.sites.title')}</Heading>
      <Text tone="muted">{t('knowledge.sites.intro')}</Text>
      <form onSubmit={add} className="qm-row" data-testid="knowledge-add-site">
        <Field label={t('knowledge.sites.url')} hint={t('knowledge.sites.urlHint')}>
          {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} type="text" inputMode="url" autoComplete="url" required value={url} onChange={(e) => setUrl(e.target.value)} />}
        </Field>
        <Field label={t('knowledge.sites.name')}>
          {({ id }) => <TextInput id={id} type="text" value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field label={t('knowledge.sites.schedule')}>
          {({ id }) => (
            <Select id={id} value={schedule} onChange={(e) => setSchedule(e.target.value)}>
              <option value="">{t('knowledge.sites.schedule.none')}</option>
              {RECRAWL_OPTIONS.map((d) => <option key={d} value={String(d)}>{t('knowledge.sites.schedule.days', { days: d })}</option>)}
            </Select>
          )}
        </Field>
        <Button type="submit" variant="primary" disabled={busy || !url.trim()}>{t('knowledge.sites.add')}</Button>
      </form>
      {sites.status === 'loading' ? <Loading label={t('common.loading')} /> : sites.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : sites.data.length === 0 ? (
        <EmptyState title={t('knowledge.sites.empty')} />
      ) : (
        <ul className="qm-list" data-testid="knowledge-sites">
          {sites.data.map((s) => <SiteItem key={s.id} site={s} busy={busy} run={run} canRemove={canRemove} />)}
        </ul>
      )}
    </Panel>
  );
}

function SiteItem({ site: s, busy, run, canRemove }: { site: Site; busy: boolean; run: Run; canRemove: boolean }) {
  const { t } = useApp();
  const f = useFormat();
  const [open, setOpen] = useState(false);
  const active = s.status === 'crawling' || s.status === 'queued';
  const base = `/v1/knowledge/sites/${encodeURIComponent(s.id)}` as const;
  const reasonKey = s.statusReason ? `knowledge.sites.reason.${s.statusReason}` : null;
  return (
    <li className="qm-list__item" data-site={s.id}>
      <div>
        <Heading level={3}><bdi>{f.text(s.rootUrl)}</bdi></Heading>
        <div className="qm-row">
          <Badge tone={siteTone(s.status)}>{t(`knowledge.sites.status.${s.status}`)}</Badge>
          <span>{active ? t('knowledge.sites.progress', { done: pagesDone(s), found: s.pages.found }) : t('knowledge.sites.pagesCount', { ...s.pages })}</span>
        </div>
        {reasonKey && t(reasonKey) !== reasonKey ? <Text tone="muted">{t(reasonKey)}</Text> : null}
        {s.nextCrawlAt ? <Text tone="muted">{t('knowledge.sites.nextCrawl', { date: f.date(s.nextCrawlAt) })}</Text> : null}
      </div>
      <div className="qm-row">
        <Select aria-label={t('knowledge.sites.schedule')} value={s.recrawlDays ? String(s.recrawlDays) : ''} disabled={busy}
          onChange={(e) => { const v = e.target.value; void run(() => bff(base, { method: 'PATCH', body: JSON.stringify({ recrawlDays: v ? Number(v) : null }) }), 'knowledge.sites.scheduleSaved'); }}>
          <option value="">{t('knowledge.sites.schedule.none')}</option>
          {RECRAWL_OPTIONS.map((d) => <option key={d} value={String(d)}>{t('knowledge.sites.schedule.days', { days: d })}</option>)}
        </Select>
        {active ? (
          <Button onClick={() => void run(() => bffJson(`${base}/cancel`, 'POST', {}), 'knowledge.sites.cancelled')} disabled={busy}>{t('knowledge.sites.cancel')}</Button>
        ) : (
          <Button onClick={() => void run(() => bffJson(`${base}/recrawl`, 'POST', {}), 'knowledge.sites.recrawlQueued')} disabled={busy}>{t('knowledge.sites.recrawl')}</Button>
        )}
        <Button aria-expanded={open} onClick={() => setOpen((v) => !v)}>{t(open ? 'knowledge.sites.hidePages' : 'knowledge.sites.showPages')}</Button>
        {canRemove ? (
          <Button onClick={() => { if (window.confirm(t('knowledge.sites.removeConfirm'))) void run(() => bffJson(base, 'DELETE'), 'knowledge.sites.removed'); }} disabled={busy}>{t('knowledge.sites.remove')}</Button>
        ) : null}
      </div>
      {open ? <SitePages siteId={s.id} /> : null}
    </li>
  );
}

function SitePages({ siteId }: { siteId: string }) {
  const { t } = useApp();
  const f = useFormat();
  const res = useResource<SitePage[]>(`/v1/knowledge/sites/${encodeURIComponent(siteId)}/pages?limit=500`);
  if (res.status === 'loading') return <Loading label={t('common.loading')} />;
  if (res.status === 'error') return <ErrorState message={t('shell.error.load')} />;
  if (!res.data.length) return <EmptyState title={t('knowledge.pages.empty')} />;
  const known = (k: string) => t(k) !== k;
  return (
    <DataTable caption={t('knowledge.pages.title')} rows={res.data} columns={[
      { key: 'url', header: t('knowledge.pages.col.page'), render: (p) => <bdi>{f.text(p.url)}</bdi> },
      { key: 'status', header: t('knowledge.pages.col.status'), render: (p) => t(`knowledge.pages.status.${p.status}`) },
      { key: 'reason', header: t('knowledge.pages.col.reason'), render: (p) => { const r = pageReasonKey(p.reason, known); return r ? t(r.key, r.params) : ''; } },
    ]} />
  );
}

function SourcesPanel({ sources, busy, run, canRemove, ocr }: { sources: ReturnType<typeof useResource<Source[]>>; busy: boolean; run: Run; canRemove: boolean; ocr: boolean }) {
  const { t } = useApp();
  const f = useFormat();
  let body;
  if (sources.status === 'loading') body = <Loading label={t('common.loading')} />;
  else if (sources.status === 'error') body = <ErrorState message={t('shell.error.load')} />;
  else if (!sources.data.length) body = <EmptyState title={t('knowledge.sources.empty')} />;
  else body = (
    <DataTable caption={t('knowledge.sources.title')} rows={sources.data} columns={[
      { key: 'title', header: t('knowledge.sources.col.title'), render: (s) => <bdi>{f.text(s.title)}</bdi> },
      { key: 'type', header: t('knowledge.sources.col.type'), render: (s) => t(`knowledge.type.${s.type}`) },
      {
        key: 'status', header: t('knowledge.sources.col.status'), render: (s) => (
          <div>
            <Badge tone={s.status === 'ready' ? 'success' : s.status === 'failed' || s.status === 'quarantined' ? 'error' : 'accent'}>{t(`knowledge.status.${s.status}`)}</Badge>
            {s.ocr ? <OcrLine ocr={s.ocr} /> : null}
          </div>
        ),
      },
      { key: 'chunks', header: t('knowledge.sources.col.chunks'), numeric: true, render: (s) => f.number(s.chunkCount) },
      { key: 'updated', header: t('knowledge.sources.col.updated'), render: (s) => (s.lastIngestedAt ? f.date(s.lastIngestedAt) : t('knowledge.sources.never')) },
      {
        key: 'actions', header: t('knowledge.sources.col.actions'), render: (s) => (
          <div className="qm-row">
            {ocr && s.ocr?.status === 'failed' ? (
              <Button onClick={() => void run(() => bffJson(`/v1/knowledge/sources/${encodeURIComponent(s.id)}/ocr`, 'POST', {}), 'knowledge.ocr.retryQueued')} disabled={busy}>{t('knowledge.ocr.retry')}</Button>
            ) : null}
            {canRemove ? (
              <Button aria-label={`${t('knowledge.sources.remove')} ${s.title}`} onClick={() => { if (window.confirm(t('knowledge.delete.confirm'))) void run(() => bffJson(`/v1/knowledge/sources/${encodeURIComponent(s.id)}`, 'DELETE'), 'knowledge.sources.removed'); }} disabled={busy}>{t('knowledge.sources.remove')}</Button>
            ) : null}
          </div>
        ),
      },
    ]} />
  );
  return (
    <Panel aria-labelledby="sources-title">
      <Heading level={2} id="sources-title">{t('knowledge.sources.title')}</Heading>
      {body}
    </Panel>
  );
}

function OcrLine({ ocr }: { ocr: Ocr }) {
  const { t } = useApp();
  const reasonKey = ocr.reason ? `knowledge.ocr.reason.${ocr.reason}` : null;
  return (
    <Text tone="muted">
      {t('knowledge.ocr.label')}: {t(`knowledge.ocr.status.${ocr.status}`)}
      {ocr.pages ? ` · ${t('knowledge.ocr.pages', { pages: ocr.pages })}` : ''}
      {reasonKey && t(reasonKey) !== reasonKey ? ` · ${t(reasonKey)}` : ''}
    </Text>
  );
}
